import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { api, eur } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

const norm = (s) => String(s || "").toUpperCase().replace(/O/g, "0");

export default function Confirmation() {
  const { code } = useParams();
  const { state } = useLocation();
  const { t } = useI18n();
  const [booking, setBooking] = useState(state?.booking || null);

  useEffect(() => {
    api
      .get(`/bookings/code/${code}`)
      .then(({ data }) => setBooking(data))
      .catch(() =>
        api
          .get("/bookings/mine")
          .then(({ data }) => {
            const b = (data.bookings || []).find((x) => norm(x.code) === norm(code));
            if (b) setBooking(b);
          })
          .catch(() => {})
      );
  }, [code]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-3xl px-5 py-16 lg:px-10 lg:py-24">
        <div className="rounded-xl border border-slate-200 bg-white p-8 ambient fade-up" data-testid="confirmation-card">
          <span className="grid h-12 w-12 place-items-center rounded-full bg-emerald-50 text-emerald-600">
            <CheckCircle2 size={26} />
          </span>
          <h1 className="mt-6 font-display text-3xl tracking-tight sm:text-4xl">{t("confirmed.title")}</h1>
          <p className="mt-3 text-base text-slate-600">{t("confirmed.sub")}</p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-slate-200 p-5">
              <p className="label-eyebrow">{t("confirmed.code")}</p>
              <p className="mt-2 font-display text-2xl tracking-wide" data-testid="booking-code">
                {booking?.code || code}
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 p-5">
              <p className="label-eyebrow">{t("confirmed.pin")}</p>
              <p className="mt-2 font-display text-2xl tracking-wide" data-testid="booking-pin">
                {booking?.pin || "—"}
              </p>
            </div>
          </div>

          {booking && (
            <div className="mt-8 space-y-3 border-t border-slate-200 pt-6 text-sm">
              <p className="font-medium text-slate-900">
                {booking.origin?.name} <ArrowRight size={13} className="inline text-slate-400" />{" "}
                {booking.destination?.name}
              </p>
              <p className="text-slate-600">
                {booking.vehicle_class_name} · {booking.pickup_at?.replace("T", " · ")} · {booking.pax}{" "}
                {t("results.pax")}
              </p>
              <p className="font-display text-2xl text-sea">{eur(booking.price_eur)}</p>
            </div>
          )}

          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/bookings">
              <Button data-testid="go-to-bookings-btn" className="bg-sea hover:bg-sea-dark">
                {t("confirmed.mine")}
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" data-testid="book-again-btn">
                {t("confirmed.again")}
              </Button>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
